import { Link } from "react-router-dom";
import {
  ArrowRight,
  BellRing,
  BookOpenCheck,
  Camera,
  CalendarCheck2,
  CheckCircle2,
  Clock3,
  DraftingCompass,
  HardHat,
  ImageIcon,
  Landmark,
  Link2,
  MessageSquareText,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Users,
  Wrench,
  PenLine,
  BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
 * "Full-Spectrum Builder OS" landing sections:
 *   BookingLoopSection       — Smart Booking Loop (automated booking engine) + Gantt mock
 *   ClientExperienceSection  — automated client portal
 *   QualityComplianceSection — Defect-to-Dispatch + Site IQ Expert
 *   ProfitProtectionSection  — instant variation signing
 *   FeatureCategoriesGrid    — Smart Estimating · Intelligent Site Ops · Pro Financials
 *   FinalValueBanner         — closing value statement above the footer
 * All mock-ups are CSS-only with fictional names and numbers.
 * ------------------------------------------------------------------------- */

const Container = ({ className = "", children }) => (
  <div className={cn("mx-auto w-full max-w-[1120px] px-4 sm:px-6 lg:px-8", className)}>{children}</div>
);

const Kicker = ({ children, className }) => <p className={cn("text-xs font-semibold uppercase tracking-[0.18em] text-primary", className)}>{children}</p>;

const FRAME = "rounded-[16px] border border-[rgba(11,42,74,0.14)] bg-white shadow-[var(--shadow-lift)]";
const TINT = "border-y border-border bg-[hsl(210_28%_96%)]";

/* =========================================================================================
 * 1. Smart Booking Loop
 * ======================================================================================= */
const WEEKS = ["6 Oct", "13 Oct", "20 Oct", "27 Oct", "3 Nov", "10 Nov", "17 Nov", "24 Nov"];

const GANTT_ROWS = [
  { task: "Slab pour", trade: "R&J Concreting", start: 1, span: 1, state: "done" },
  { task: "Frame & trusses", trade: "J. Smith Carpentry", start: 2, span: 2, state: "confirmed" },
  { task: "Roof plumbing", trade: "Apex Roofing", start: 4, span: 1, state: "confirmed" },
  { task: "Wall & floor tiling", trade: "Sam's Tiling", start: 6, span: 2, state: "sent" },
  { task: "Kitchen install", trade: "Coastal Joinery", start: 7, span: 1, state: "proposed" },
];

const ROW_STATE = {
  done: { label: "Complete", cls: "border-border bg-background text-muted-foreground", bar: "bg-foreground/25", icon: CheckCircle2 },
  confirmed: { label: "Confirmed", cls: "border-[hsl(var(--chart-received)/0.35)] bg-[hsl(var(--chart-received)/0.10)] text-[hsl(var(--chart-received))]", bar: "bg-primary", icon: CheckCircle2 },
  sent: { label: "Booking request sent", cls: "border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]", bar: "bg-primary/45 [background-image:repeating-linear-gradient(135deg,transparent_0_5px,rgba(255,255,255,0.45)_5px_9px)]", icon: Send },
  proposed: { label: "New date proposed", cls: "border-primary/30 bg-accent text-primary", bar: "bg-primary/45 [background-image:repeating-linear-gradient(135deg,transparent_0_5px,rgba(255,255,255,0.45)_5px_9px)]", icon: CalendarCheck2 },
};

export const BookingGanttMockup = ({ className }) => (
  <div
    className={cn("relative overflow-hidden p-4 sm:p-5", FRAME, className)}
    role="img"
    aria-label="BuiltOS schedule with a 'Booking request sent' badge next to the tiler, and a confirmation notice"
    data-testid="landing-booking-gantt-mockup"
  >
    <div className="flex items-center justify-between gap-3">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
        <CalendarCheck2 className="h-3.5 w-3.5 text-primary" /> <span className="whitespace-nowrap">Schedule · 42 Cadorna St</span>
      </p>
      <span className="hidden items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-flex">
        <Link2 className="h-3 w-3 text-primary" /> 3 trades booked from this view today
      </span>
    </div>

    {/* week header */}
    <div className="mt-3 grid grid-cols-[minmax(0,0.95fr)_minmax(0,2.05fr)] gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2.2fr)]">
      <span />
      <div className="grid grid-cols-8 text-[9px] font-medium text-muted-foreground">
        {WEEKS.map((w) => (
          <span key={w} className="truncate border-l border-border/70 pl-1">
            {w}
          </span>
        ))}
      </div>
    </div>

    <ul className="mt-1 divide-y divide-border/60">
      {GANTT_ROWS.map((r) => {
        const st = ROW_STATE[r.state];
        const Icon = st.icon;
        const highlight = r.state === "sent";
        return (
          <li
            key={r.task}
            className={cn("grid grid-cols-[minmax(0,0.95fr)_minmax(0,2.05fr)] items-center gap-3 py-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2.2fr)]", highlight && "-mx-2 rounded-lg bg-accent/60 px-2")}
            data-testid={`landing-booking-gantt-row-${r.state}`}
          >
            <div className="min-w-0">
              <p className="truncate text-[11px] font-semibold text-foreground">{r.task}</p>
              <p className="truncate text-[10px] text-muted-foreground">{r.trade}</p>
            </div>
            <div className="relative h-7">
              <div className="absolute inset-0 grid grid-cols-8">
                {WEEKS.map((w) => (
                  <span key={w} className="border-l border-border/50" aria-hidden="true" />
                ))}
              </div>
              <div
                className={cn("absolute top-1/2 h-4 -translate-y-1/2 rounded-md", st.bar)}
                style={{ left: `${((r.start - 1) / 8) * 100}%`, width: `calc(${(r.span / 8) * 100}% - 4px)`, marginLeft: 2 }}
              />
              <span
                className={cn(
                  "absolute top-1/2 inline-flex -translate-y-1/2 items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[9px] font-semibold shadow-[var(--shadow-card)]",
                  st.cls,
                  highlight && "px-2 py-1 text-[10px] ring-2 ring-[hsl(var(--warning)/0.25)]"
                )}
                style={
                  r.start + r.span > 6
                    ? { right: `calc(${(1 - (r.start - 1) / 8) * 100}% + 6px)` } // bar sits right → badge to its left
                    : { left: `calc(${((r.start - 1 + r.span) / 8) * 100}% + 6px)` }
                }
                data-testid={highlight ? "landing-booking-request-sent-badge" : undefined}
              >
                {highlight && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[hsl(var(--warning))]" aria-hidden="true" />}
                <Icon className="h-3 w-3" /> {st.label}
              </span>
            </div>
          </li>
        );
      })}
    </ul>

    {/* confirmation notice */}
    <div
      className="mt-3 flex items-start gap-2.5 rounded-xl border border-[hsl(var(--chart-received)/0.3)] bg-[hsl(var(--chart-received)/0.08)] px-3 py-2"
      data-testid="landing-booking-confirmation-toast"
    >
      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[hsl(var(--chart-received))] shadow-[var(--shadow-card)]">
        <BellRing className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 text-[11px] leading-5">
        <p className="font-semibold text-foreground">Apex Roofing confirmed Mon 27 Oct</p>
        <p className="text-muted-foreground">Live schedule updated · downstream trades notified · nothing for you to do.</p>
      </div>
      <span className="ml-auto shrink-0 font-mono-nums text-[10px] text-muted-foreground">just now</span>
    </div>
  </div>
);

const BOOKING_PHASES = [
  {
    n: "01",
    icon: Send,
    title: "Send from the schedule",
    body: "Pick the task, pick the trade or supplier, click once. BuiltOS sends a booking request straight from your project schedule — no phone tag.",
  },
  {
    n: "02",
    icon: Link2,
    title: "Trades confirm on a link",
    body: "Your tiler gets a secure link on their phone: confirm the date in one tap, or propose a new one. No app to download, no login.",
  },
  {
    n: "03",
    icon: RefreshCw,
    title: "Schedule updates itself",
    body: "Once confirmed, BuiltOS updates your live schedule, ripples the change to dependent trades and notifies you of the confirmation.",
  },
];

export const BookingLoopSection = () => (
  <section id="booking-loop" className="py-16 sm:py-20" data-testid="landing-booking-loop">
    <Container>
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Kicker>Automated booking engine</Kicker>
          <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.02em] sm:text-4xl" data-testid="landing-booking-loop-headline">
            Stop making a dozen phone calls just to book a tiler.
          </h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground" data-testid="landing-booking-loop-body">
            The Smart Booking Loop turns your schedule into the booking system. Every trade and supplier is requested, confirmed
            and locked into the program without you leaving the Gantt — and without a single voicemail.
          </p>
          <ol className="mt-7 space-y-4">
            {BOOKING_PHASES.map(({ n, icon: Icon, title, body }) => (
              <li key={n} className="flex items-start gap-4" data-testid={`landing-booking-phase-${n}`}>
                <span className="relative mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[var(--shadow-card)] ring-1 ring-primary/20">
                  <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-primary/30 bg-white px-1 font-mono-nums text-[10px] font-semibold text-primary">
                    {n}
                  </span>
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground">{title}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="lg:col-span-7">
          <BookingGanttMockup />
        </div>
      </div>
    </Container>
  </section>
);

/* =========================================================================================
 * 2. Client experience — automated client portal
 * ======================================================================================= */
const PORTAL_STAGES = [
  { name: "Slab", pct: 100 },
  { name: "Frame", pct: 100 },
  { name: "Lock-up", pct: 62, live: true },
  { name: "Fixing", pct: 0 },
  { name: "Completion", pct: 0 },
];

const PORTAL_PHOTOS = [
  { label: "Roof trusses", tone: "bg-[hsl(210_28%_88%)]" },
  { label: "Frame inspection", tone: "bg-[hsl(var(--primary)/0.18)]" },
  { label: "Windows delivered", tone: "bg-[hsl(var(--chart-received)/0.18)]" },
];

export const ClientPortalMockup = ({ className }) => (
  <div
    className={cn("relative overflow-hidden p-4 sm:p-5", FRAME, className)}
    role="img"
    aria-label="BuiltOS client portal showing stage progress and today's site diary photos shared automatically"
    data-testid="landing-client-portal-mockup"
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
          <Users className="h-3.5 w-3.5" /> Client portal
        </p>
        <p className="mt-1 font-heading text-base font-semibold text-foreground">The Nairs · 42 Cadorna St</p>
      </div>
      <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--chart-received)/0.12)] px-2 py-0.5 text-[9px] font-semibold text-[hsl(var(--chart-received))]">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[hsl(var(--chart-received))]" aria-hidden="true" /> updated 4:52 pm
      </span>
    </div>

    {/* stage progress */}
    <div className="mt-4 rounded-xl border border-border bg-background/60 p-3">
      <div className="flex items-center justify-between text-[11px]">
        <p className="font-semibold text-foreground">Build progress</p>
        <p className="font-mono-nums text-muted-foreground">
          stage 3 of 5 · <span className="font-semibold text-foreground">Lock-up</span>
        </p>
      </div>
      <ol className="mt-2.5 grid grid-cols-5 gap-1.5">
        {PORTAL_STAGES.map((s) => (
          <li key={s.name} className="min-w-0">
            <div className="h-1.5 overflow-hidden rounded-full bg-foreground/[0.08]">
              <div className={cn("h-full rounded-full", s.live ? "bg-[hsl(var(--chart-received))]" : "bg-primary")} style={{ width: `${s.pct}%` }} />
            </div>
            <p className={cn("mt-1 truncate text-[9px] font-medium", s.live ? "text-[hsl(var(--chart-received))]" : s.pct ? "text-foreground" : "text-muted-foreground")}>{s.name}</p>
          </li>
        ))}
      </ol>
    </div>

    {/* today's update — flows from the site diary */}
    <div className="mt-3 rounded-xl border border-border bg-card p-3 shadow-[var(--shadow-card)]" data-testid="landing-client-portal-update">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
          <Camera className="h-3.5 w-3.5 text-primary" /> Today on site
        </p>
        <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-accent px-2 py-0.5 text-[9px] font-semibold text-primary">
          <RefreshCw className="h-3 w-3" /> auto-shared from the site diary
        </span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        {PORTAL_PHOTOS.map((p) => (
          <figure key={p.label} className={cn("relative aspect-[4/3] overflow-hidden rounded-lg", p.tone)}>
            <ImageIcon className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 text-foreground/25" aria-hidden="true" />
            <figcaption className="absolute inset-x-0 bottom-0 truncate bg-foreground/70 px-1.5 py-0.5 text-[8px] font-medium text-white">{p.label}</figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-2 text-[11px] leading-5 text-foreground/90">
        Frame inspection passed this morning. Roof trusses are up and the windows have landed — lock-up is on track for the 14th.
      </p>
      <p className="mt-1 text-[10px] text-muted-foreground">Posted by your supervisor, Marcus · 3 photos · read by Priya at 5:10 pm</p>
    </div>
  </div>
);

const CLIENT_POINTS = [
  "Stage updates and progress photos publish to the portal the moment the diary is saved",
  "Clients see what's next, what's approved and what's been paid — without calling you",
  "Variations, claims and documents all live in one branded place under your logo",
];

export const ClientExperienceSection = () => (
  <section id="client-experience" className="py-16 sm:py-20" data-testid="landing-client-experience">
    <Container>
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
        <div className="order-2 lg:order-1 lg:col-span-6">
          <ClientPortalMockup />
        </div>
        <div className="order-1 lg:order-2 lg:col-span-6">
          <Kicker>Client experience</Kicker>
          <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.02em] sm:text-4xl" data-testid="landing-client-experience-headline">
            Built to Impress Your Clients.
          </h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground" data-testid="landing-client-experience-body">
            <span className="font-semibold text-foreground">Automated Client Portal.</span> Site diary photos and stage updates flow
            automatically from your supervisor's phone to your client's — the same day, every day. No Friday email round-up, no
            "how's it going?" texts. Just the most transparent builder they've ever worked with.
          </p>
          <ul className="mt-6 space-y-3">
            {CLIENT_POINTS.map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm leading-6 text-foreground/90">
                <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[hsl(var(--chart-received))]" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Container>
  </section>
);

/* =========================================================================================
 * 3. Quality & compliance — Defect-to-Dispatch + Site IQ Expert
 * ======================================================================================= */
const DEFECT_TRAIL = [
  { icon: Camera, text: "Snapped on site", when: "9:12 am", done: true },
  { icon: Send, text: "Sent to Sam's Tiling", when: "9:13 am", done: true },
  { icon: Wrench, text: "Rectified · photo attached", when: "2:40 pm", done: true },
  { icon: BadgeCheck, text: "Verified & closed", when: "3:05 pm", done: false },
];

const DefectDispatchMockup = () => (
  <div className="rounded-xl border border-border bg-background/60 p-3" role="img" aria-label="A defect photographed on site, dispatched to the trade and rectified the same day" data-testid="landing-defect-dispatch-mockup">
    <div className="flex items-start gap-3">
      <figure className="relative h-16 w-20 shrink-0 overflow-hidden rounded-lg bg-[hsl(210_28%_88%)]">
        <ImageIcon className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 text-foreground/25" aria-hidden="true" />
        <span className="absolute left-1 top-1 rounded-full bg-[hsl(var(--chart-expenses))] px-1.5 py-0.5 text-[8px] font-semibold text-white">DEF-014</span>
      </figure>
      <div className="min-w-0">
        <p className="truncate text-[11px] font-semibold text-foreground">Chipped floor tile — ensuite</p>
        <p className="truncate text-[10px] text-muted-foreground">42 Cadorna St · Sam's Tiling · due before handover</p>
        <span className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-[hsl(var(--chart-received)/0.35)] bg-[hsl(var(--chart-received)/0.10)] px-2 py-0.5 text-[9px] font-semibold text-[hsl(var(--chart-received))]">
          <CheckCircle2 className="h-3 w-3" /> Fixed same day
        </span>
      </div>
    </div>
    <ol className="mt-3 space-y-1.5">
      {DEFECT_TRAIL.map(({ icon: Icon, text, when, done }) => (
        <li key={text} className="flex items-center gap-2 text-[10px]">
          <span className={cn("inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full", done ? "bg-accent text-primary" : "border border-dashed border-border text-muted-foreground")}>
            <Icon className="h-3 w-3" />
          </span>
          <span className={cn("truncate", done ? "font-medium text-foreground" : "text-muted-foreground")}>{text}</span>
          <span className="ml-auto shrink-0 font-mono-nums text-muted-foreground">{when}</span>
        </li>
      ))}
    </ol>
  </div>
);

const SiteIQMockup = () => (
  <div className="rounded-xl border border-border bg-background/60 p-3" role="img" aria-label="Site IQ Expert answering a National Construction Code question on site" data-testid="landing-site-iq-mockup">
    <div className="flex items-center justify-between">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
        <Sparkles className="h-3.5 w-3.5 text-primary" /> Site IQ Expert
      </p>
      <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[9px] font-medium text-muted-foreground">NCC 2022 · AS/NZS</span>
    </div>
    <div className="mt-2.5 space-y-2">
      <p className="ml-8 rounded-2xl rounded-tr-sm bg-primary px-3 py-2 text-[11px] leading-5 text-primary-foreground">
        Minimum balustrade height for a deck 1.4 m above ground?
      </p>
      <div className="mr-6 rounded-2xl rounded-tl-sm border border-border bg-card px-3 py-2 text-[11px] leading-5 text-foreground/90 shadow-[var(--shadow-card)]">
        <p>
          <span className="font-semibold text-foreground">1,000 mm minimum</span> — a barrier is required wherever the fall is more than 1 m,
          measured from the finished deck surface. Openings can't allow a 125 mm sphere to pass, and horizontal climbable elements are
          only restricted once you're above 4 m.
        </p>
        <p className="mt-1.5 flex flex-wrap gap-1.5">
          {["NCC 2022 Vol. 2 · H5D3", "ABCB Housing Provisions · 11.3"].map((ref) => (
            <span key={ref} className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[9px] font-semibold text-primary">
              <BookOpenCheck className="h-3 w-3" /> {ref}
            </span>
          ))}
        </p>
      </div>
    </div>
    <p className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
      <MessageSquareText className="h-3 w-3" /> Ask in plain English · answers cite the clause · saved to the site diary
    </p>
  </div>
);

const QUALITY_FEATURES = [
  {
    key: "defect-to-dispatch",
    icon: Wrench,
    title: "Defect-to-Dispatch",
    quote: "Snap a photo, notify the trade, fix the issue.",
    body: "Integrated rectification management: every defect is photographed, assigned and chased from one list, with the trade's fix photo closing the loop before handover.",
    Mock: DefectDispatchMockup,
  },
  {
    key: "site-iq-expert",
    icon: BookOpenCheck,
    title: "Site IQ Expert",
    quote: "A live AI expert on the NCC and Australian Standards in every supervisor's pocket.",
    body: "Ask about set-downs, step heights, fire separation or waterproofing and get a clause-cited answer on site — before the inspector does.",
    Mock: SiteIQMockup,
  },
];

export const QualityComplianceSection = () => (
  <section id="quality" className={cn("py-16 sm:py-20", TINT)} data-testid="landing-quality">
    <Container>
      <div className="max-w-[60ch]">
        <Kicker>Quality & compliance</Kicker>
        <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.02em] sm:text-4xl" data-testid="landing-quality-headline">
          Zero-Friction Quality Control.
        </h2>
        <p className="mt-3 text-base leading-7 text-muted-foreground">
          Quality on site shouldn't mean a clipboard, a group chat and a Saturday of follow-ups. Two tools your supervisors will actually
          reach for.
        </p>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
        {QUALITY_FEATURES.map(({ key, icon: Icon, title, quote, body, Mock }) => (
          <article
            key={key}
            className="group flex min-w-0 flex-col rounded-[16px] border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-[box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-[var(--shadow-lift)]"
            data-testid={`landing-quality-${key}-card`}
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[var(--shadow-card)] ring-1 ring-primary/20 transition-transform duration-150 group-hover:scale-105">
              <Icon className="h-6 w-6" strokeWidth={1.75} />
            </span>
            <h3 className="mt-5 font-heading text-xl font-semibold">{title}</h3>
            <p className="mt-2 border-l-2 border-primary pl-3 text-[15px] font-semibold leading-6 text-foreground">“{quote}”</p>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{body}</p>
            <div className="mt-auto pt-5">
              <Mock />
            </div>
          </article>
        ))}
      </div>
    </Container>
  </section>
);

/* =========================================================================================
 * 4. Profit protection — instant variation signing
 * ======================================================================================= */
export const VariationSigningMockup = ({ className }) => (
  <div
    className={cn("relative overflow-hidden p-4 sm:p-5", FRAME, className)}
    role="img"
    aria-label="A variation drafted and signed on site in BuiltOS, with the invoice ready"
    data-testid="landing-variation-signing-mockup"
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Contract variation</p>
        <p className="mt-0.5 font-heading text-base font-semibold text-foreground">VAR-004 · Upgrade to 40 mm stone benchtops</p>
        <p className="text-[10px] text-muted-foreground">42 Cadorna St · drafted on site · Tue 2:09 pm</p>
      </div>
      <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--chart-received)/0.35)] bg-[hsl(var(--chart-received)/0.10)] px-2 py-0.5 text-[9px] font-semibold text-[hsl(var(--chart-received))]">
        <BadgeCheck className="h-3 w-3" /> Approved
      </span>
    </div>

    <ul className="mt-3 divide-y divide-border/70 rounded-xl border border-border bg-background/60 px-3 text-[11px]">
      {[
        ["Caesarstone 40 mm — 12.5 m² supply", "$8,000.00"],
        ["Template, fabricate & install", "$2,400.00"],
        ["Credit: laminate allowance", "-$1,800.00"],
      ].map(([d, a]) => (
        <li key={d} className="flex items-center justify-between gap-3 py-1.5">
          <span className="truncate text-foreground/90">{d}</span>
          <span className="shrink-0 font-mono-nums font-medium text-foreground">{a}</span>
        </li>
      ))}
      <li className="flex items-center justify-between gap-3 py-2 font-semibold">
        <span className="text-foreground">Total inc GST</span>
        <span className="font-mono-nums text-primary">+$9,460.00</span>
      </li>
    </ul>

    <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
      <div className="rounded-xl border border-border bg-card p-3 shadow-[var(--shadow-card)]">
        <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Client signature</p>
        <svg viewBox="0 0 220 44" className="mt-1 h-10 w-full text-primary" aria-hidden="true">
          <path
            d="M6 30 C 18 6, 26 8, 30 26 S 44 40, 54 22 C 60 10, 66 12, 70 24 S 84 36, 96 18 C 104 8, 110 12, 116 24 S 130 34, 142 20 C 150 10, 158 14, 166 24 S 184 30, 212 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <div className="flex items-center justify-between text-[10px]">
          <span className="font-medium text-foreground">Priya Nair</span>
          <span className="font-mono-nums text-muted-foreground">signed on site · 2:14 pm</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-[10px] font-semibold text-primary-foreground shadow-[var(--shadow-card)]">
          <CheckCircle2 className="h-3.5 w-3.5" /> Invoice INV-0115 ready
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-[10px] font-medium text-muted-foreground">
          <Clock3 className="h-3.5 w-3.5 text-primary" /> Draft → signed in 5 min
        </span>
      </div>
    </div>
  </div>
);

export const ProfitProtectionSection = () => (
  <section id="profit-protection" className="py-16 sm:py-20" data-testid="landing-profit-protection">
    <Container>
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Kicker>Profit protection</Kicker>
          <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.02em] sm:text-4xl" data-testid="landing-profit-protection-headline">
            Instant Variation Signing.
          </h2>
          <blockquote className="mt-4 border-l-2 border-primary pl-4 font-heading text-lg font-semibold leading-snug text-foreground sm:text-xl" data-testid="landing-profit-protection-hook">
            “Stop losing money on handshake deals.”
          </blockquote>
          <p className="mt-4 text-base leading-7 text-muted-foreground" data-testid="landing-profit-protection-body">
            Digital variation drafting and signing on the spot. Price the change from your rate book while you're standing in the
            kitchen, hand the client your phone to sign, and the variation is approved, filed and invoiced before you're back in the ute.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              { icon: PenLine, text: "Draft from the schedule or the site diary — categorised lines, GST and margin applied automatically" },
              { icon: ShieldCheck, text: "Signed approval with name, time and signature stored against the job — no more 'we never agreed to that'" },
              { icon: Landmark, text: "Approved variations flow straight to invoicing, cashflow and Xero" },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 text-sm leading-6 text-foreground/90">
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7">
          <VariationSigningMockup />
        </div>
      </div>
    </Container>
  </section>
);

/* =========================================================================================
 * 5. Feature categories — Smart Estimating · Intelligent Site Ops · Pro Financials
 * ======================================================================================= */
export const FEATURE_CATEGORIES = [
  {
    key: "smart-estimating",
    icon: DraftingCompass,
    title: "Smart Estimating",
    tagline: "From first sketch to a signed quote in an afternoon.",
    features: [
      "AI Vision Takeoff — plan sets to a priced BOQ in minutes",
      "Your own rate book, cost centres and locked margin on every line",
      "Branded quotes with online acceptance and an automatic deposit invoice",
      "Revision detection files every new plan set against the job",
      "Organic learning: every processed invoice sharpens the next estimate",
    ],
  },
  {
    key: "intelligent-site-ops",
    icon: HardHat,
    title: "Intelligent Site Ops",
    tagline: "The site runs from a phone your supervisors actually use.",
    features: [
      "Ripple-effect scheduler with the Smart Booking Loop for trades and suppliers",
      "Site diary with photos that publish straight to the client portal",
      "Defect-to-Dispatch rectification management",
      "Site IQ Expert — clause-cited NCC and Australian Standards answers",
      "Daily Site Pulse: AI suggestions ranked by dollars saved",
    ],
  },
  {
    key: "pro-financials",
    icon: Landmark,
    title: "Pro Financials",
    tagline: "A finance centre that guards your 20% margin every day.",
    features: [
      "Live two-way Xero sync with bank feeds mapped to job cost centres",
      "Margin Guard alerts before a stage starts to bleed",
      "Progress claims, variation invoices and credit notes in a boutique layout",
      "Instant variation signing that flows straight to invoicing",
      "Supplier bill inbox, PO matching and live cashflow",
    ],
  },
];

export const FeatureCategoriesGrid = () => (
  <section id="features" className={cn("py-16 sm:py-20", TINT)} data-testid="landing-features">
    <Container>
      <div className="max-w-[60ch]">
        <Kicker>The full-spectrum Builder OS</Kicker>
        <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.02em] sm:text-4xl" data-testid="landing-features-headline">
          Three disciplines. One operating system.
        </h2>
        <p className="mt-3 text-base leading-7 text-muted-foreground">
          Pricing, site and money — the three places builders lose their evenings. Each is a first-class part of BuiltOS, not a bolt-on,
          and they all share the same jobs, trades and numbers.
        </p>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-3">
        {FEATURE_CATEGORIES.map(({ key, icon: Icon, title, tagline, features }) => (
          <article
            key={key}
            className="group flex min-w-0 flex-col rounded-[16px] border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-[box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-[var(--shadow-lift)]"
            data-testid={`landing-feature-category-${key}`}
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[var(--shadow-card)] ring-1 ring-primary/20 transition-transform duration-150 group-hover:scale-105">
              <Icon className="h-6 w-6" strokeWidth={1.75} />
            </span>
            <h3 className="mt-5 font-heading text-xl font-semibold">{title}</h3>
            <p className="mt-1.5 text-[15px] font-medium leading-6 text-foreground/90">{tagline}</p>
            <ul className="mt-4 space-y-2.5 border-t border-border pt-4">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm leading-6 text-muted-foreground">
                  <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[hsl(var(--chart-received))]" /> {f}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </Container>
  </section>
);

/* =========================================================================================
 * 6. Final value statement — banner above the footer
 * ======================================================================================= */
export const FinalValueBanner = ({ cta = "Get Started" }) => (
  <section className="bg-primary py-12 text-primary-foreground sm:py-16" data-testid="landing-value-banner">
    <Container className="flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between">
      <div className="max-w-[62ch]">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground/70">The only tool you need</p>
        <h2 className="mt-2 font-heading text-2xl font-semibold leading-tight tracking-[-0.02em] sm:text-3xl lg:text-[2.25rem]" data-testid="landing-value-banner-statement">
          BuiltOS: The only tool you need to run an elite building company.
        </h2>
        <p className="mt-3 text-sm leading-6 text-primary-foreground/80 sm:text-base" data-testid="landing-value-banner-body">
          Get your evenings back. Tell us about your organisation and we'll set up a BuiltOS workspace tuned to how you build —
          typically within a business day.
        </p>
      </div>
      <Button
        asChild
        size="lg"
        className="h-11 shrink-0 rounded-[10px] bg-white px-6 text-[15px] text-primary shadow-[var(--shadow-card)] transition-[box-shadow,transform,background-color] duration-150 hover:-translate-y-px hover:bg-white/90 active:translate-y-0"
        data-testid="landing-closing-get-started-button"
      >
        <Link to="/signup">
          {cta} <ArrowRight className="ml-1.5 h-4 w-4" />
        </Link>
      </Button>
    </Container>
  </section>
);
